import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_KEY = "wl_chunk_reload";

/**
 * Drop-in replacement for React.lazy that survives Vercel/CDN deploys.
 *
 * After a new deploy, the old chunk hashes (`Services-abc123.js`) get
 * deleted from the CDN. A user with the previous `index.html` open in
 * their browser still references those old hashes, so the next route
 * navigation fails with `Failed to fetch dynamically imported module`.
 *
 * This wrapper catches that specific error and reloads the page once so
 * the user gets the fresh `index.html` → fresh chunk hashes. We gate the
 * reload behind a `sessionStorage` flag so a genuinely-broken bundle on
 * the NEW deploy doesn't loop forever — if we've already reloaded once
 * and the import still fails, we let the error propagate to the error
 * boundary.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Clear the flag once any chunk loads successfully — future stale
      // deploys should re-arm the reload logic.
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // sessionStorage unavailable (private mode, embedded view, etc.)
      }
      return mod;
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
        if (!alreadyReloaded) sessionStorage.setItem(RELOAD_KEY, "1");
      } catch {
        // ignore
      }
      if (alreadyReloaded) throw err;
      window.location.reload();
      // Block React from rendering anything while the reload kicks in.
      return new Promise<never>(() => {});
    }
  });
}

/**
 * Detects the family of errors browsers throw when a dynamic `import()`
 * can't resolve its chunk URL. Covers Chromium, Firefox, and Safari.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Unable to preload CSS") ||
    err.name === "ChunkLoadError"
  );
}
